import { FC, PropsWithChildren, ReactNode, useId } from 'react';

import * as UIPopover from '@radix-ui/react-popover';

import { arrowStyle, closeWrapperStyle, contentRecipe, triggerStyle } from './Popover.css';
import { CloseIcon } from '../../icons';
import { useI18nTranslation } from '../../providers/i18n';
import { getClassNames } from '../../util/css';
import { RecipeVariants } from '../../util/css/vanilla-extract/conditionalRecipe';
import { IconButton } from '../IconButton/IconButton';
import { Text } from '../Text/Text';
import { VisuallyHidden } from '../VisuallyHidden/VisuallyHidden';

/**
 * This component is based on the popover component from radix-ui. It pops up to the top by default, and will reverse
 * side when it detects collision. Source: https://www.radix-ui.com/docs/primitives/components/popover
 */

export const Popover: FC<PropsWithChildren<Props>> = ({ size = 'large', side = 'top', trigger, children, title }) => {
  const closeText = useI18nTranslation('close');
  const titleId = useId();
  const popoverContentClassNames = getClassNames(contentRecipe({ size }));

  return (
    <UIPopover.Root>
      {typeof trigger === 'string' ? (
        <UIPopover.Trigger className={triggerStyle}>{trigger}</UIPopover.Trigger>
      ) : (
        <UIPopover.Trigger asChild>{trigger}</UIPopover.Trigger>
      )}
      <UIPopover.Content className={popoverContentClassNames} sideOffset={-5} side={side} aria-labelledby={titleId}>
        <VisuallyHidden id={titleId}>{title}</VisuallyHidden>
        <div className={closeWrapperStyle}>
          <UIPopover.Close asChild>
            <IconButton label={closeText} color="inverted">
              <CloseIcon size="small" />
            </IconButton>
          </UIPopover.Close>
        </div>
        <Text size="BodyXS" weight="bold">
          {children}
        </Text>
        <UIPopover.Arrow className={arrowStyle} offset={10} />
      </UIPopover.Content>
    </UIPopover.Root>
  );
};

type PopoverSizeVariants = RecipeVariants<typeof contentRecipe>;

interface Props extends Pick<PopoverSizeVariants, 'size'> {
  trigger: ReactNode;
  /** The title used to announce the popover dialog to screen reader users */
  title: string;
  /** The direction to which the Popover opens. If there is no space available, it will be opened to the opposite side. */
  side?: 'top' | 'right' | 'bottom' | 'left';
}
